import { Component, OnDestroy, OnInit } from '@angular/core';
import { Visitor } from '../dtos/visitor';
import { OutlookService } from './../services/outlook.service';
import { User } from '../dtos/user';
import { MailInfo } from '../dtos/mail-info';
import { snackMessage } from '../general_functions/TriggerSnackMessage';
import { NgxSpinnerService } from 'ngx-spinner';
import { SnackbarService } from 'ngx-snackbar';
import { YardService } from '../services/yard.service';
import { jsPDF } from "jspdf";
import { DatePipe } from '@angular/common';

@Component({
  selector: 'app-schedule-visitor',
  templateUrl: './schedule-visitor.component.html',
  styleUrls: ['./schedule-visitor.component.css']
})
export class ScheduleVisitorComponent implements OnInit, OnDestroy {

  users: User[] = [];
  selectedUserIndex = -1;
  previousAttendees: string[] = [];
  polling: any;
  hasUnsavedChanges = false;
  mailInfo: MailInfo;
  visitors: Visitor[] = [];

  constructor(private outlookService: OutlookService, private spinner: NgxSpinnerService, private snackbarService: SnackbarService,
    private yardService: YardService, private datePipe: DatePipe
  ) { }

  ngOnInit() {
    if (Office.context.mailbox.item) {
      this.spinner.show();
      // Check if this is an appointment
      if (Office.context.mailbox.item.itemType === Office.MailboxEnums.ItemType.Appointment) {
        this.handleAttendeesChange();
      }
      this.polling = setInterval(() => {
        this.checkAttendees();
      }, 2000);
    }
  }

  selectUser(user: User, index:number) {
    this.selectedUserIndex = index;
    if (index !== -1) {
      this.hasUnsavedChanges = true;
    }
  }

  async saveAll() {
    // Save logic for all users
    console.log('Saving all users:', this.users);
    await this.getAppointmentDetails().then((details: MailInfo) => {
      this.mailInfo = details;
    });
    this.hasUnsavedChanges = false;
    this.visitors = [];
    this.spinner.show();
    this.removeAttachments();
    this.users.filter(u => u.send == true).forEach((user, index) => {
      const visitor = this.mapUserToVisitorDto(user);
      if(visitor.id > 0) {
        this.yardService.updateVistitor(visitor).subscribe(res => {
          visitor.visitor_id = res.visitor_id;
          this.users.find(f => f.email === visitor.email).visitorId = res.id;
          this.generateAndAttachVistitorPass(visitor);
          this.visitors.push(visitor);
          if (index === (this.users.filter(f => f.send == true).length - 1)) {
            this.spinner.hide();
            snackMessage(this, "Saved successfully!", 'text-white', '#28a745');
          }
        }, error => {
          this.spinner.hide();
          snackMessage(this, "An error occurred while saving visitor details", 'text-white', '#dc3545');
        });
      } else {
        this.yardService.addVisitor(visitor).subscribe(res => {
          visitor.visitor_id = res.visitor_id;
          this.users.find(f => f.email === visitor.email).visitorId = res.id;
          this.generateAndAttachVistitorPass(visitor);
          this.visitors.push(visitor);
          if (index === (this.users.filter(f => f.send == true).length - 1)) {
            this.spinner.hide();
            snackMessage(this, "Saved successfully!", 'text-white', '#28a745');
          }
        }, error => {
          this.spinner.hide();
          snackMessage(this, "An error occurred while saving visitor details", 'text-white', '#dc3545');
        });
      }
    })
  }

  checkAttendees() {
    const currentItem = Office.context.mailbox.item;

    if (currentItem.itemType === Office.MailboxEnums.ItemType.Appointment) {
      currentItem.requiredAttendees.getAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          const currentAttendees = result.value.map(attendee => attendee.emailAddress);

          // Check if the attendees have changed
          if (this.hasAttendeesChanged(this.previousAttendees, currentAttendees)) {
            console.log('Attendees changed');

            // Handle attendee changes
            this.handleAttendeesChange();
          }

          // Update the previous attendees list
          this.previousAttendees = currentAttendees;
        } else {
          console.error('Error fetching required attendees:', result.error.message);
        }
      });
    }
  }

  hasAttendeesChanged(prevAttendees: string[], newAttendees: string[]): boolean {
    if (prevAttendees.length !== newAttendees.length) {
      return true;
    }

    for (let i = 0; i < newAttendees.length; i++) {
      if (prevAttendees[i] !== newAttendees[i]) {
        return true;
      }
    }
    return false;
  }

  handleAttendeesChange() {
    this.getAppointmentDetails().then((details: MailInfo) => {
      this.mailInfo = details;
      details.attendees.forEach(a => {
        if (!this.users.some(s => s.email === a.emailAddress)) {
          this.users.push({
            email: a.emailAddress,
            details: 'warning',
            send: true,
            company: '',
            firstname: '',
            lastname: '',
            phonenumber: ''
          });
        }
      });
      this.users = this.users.filter(f => details.attendees.some(s => s.emailAddress === f.email));
      this.spinner.hide();
      if (this.users.length < 1) {
        this.snackbarService.clear();
        snackMessage(this, "Please add at least one attendee.", 'text-white', '#dc3545');
      }
    }).catch(error => {
      console.error(error);
      this.spinner.hide();
    });
  }

  async getAppointmentDetails() {
    const item = Office.context.mailbox.item;

    return new Promise((resolve, reject) => {
      item.start.getAsync((startResult) => {
        if (startResult.status === Office.AsyncResultStatus.Succeeded) {
          item.end.getAsync((endResult) => {
            if (endResult.status === Office.AsyncResultStatus.Succeeded) {
              item.subject.getAsync((subjectResult) => {
                if (subjectResult.status === Office.AsyncResultStatus.Succeeded) {
                  item.location.getAsync((locationResult) => {
                    if (locationResult.status === Office.AsyncResultStatus.Succeeded) {
                      item.requiredAttendees.getAsync((attendeesResult) => {
                        if (attendeesResult.status === Office.AsyncResultStatus.Succeeded) {
                          resolve({
                            subject: subjectResult.value || item.normalizedSubject,
                            start: startResult.value,
                            end: endResult.value,
                            attendees: attendeesResult.value.filter(f => f.emailAddress !== Office.context.mailbox.userProfile.emailAddress),
                            location: locationResult.value,
                            user: Office.context.mailbox.userProfile.displayName,
                          } as unknown as MailInfo);
                        } else {
                          reject(attendeesResult.error);
                        }
                      });
                    } else {
                      reject(locationResult.error)
                    }
                  })
                } else {
                  reject(subjectResult.error);
                }
              })
            } else {
              reject(endResult.error);
            }
          });
        } else {
          reject(startResult.error);
        }
      });
    });
  }

  setIndicator(event: any) {
    this.users[this.selectedUserIndex].details = this.users[this.selectedUserIndex].firstname && this.users[this.selectedUserIndex].lastname ? 'success' : 'warning';
    //this.selectedUser.send = this.selectedUser.firstname && this.selectedUser.lastname ? true : false;
  }

  saveAndCloseAppointment() {
    const item = Office.context.mailbox.item;
    item.saveAsync((result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        console.log('Calling the API here on save and close');
        //this.callYourApi(result.value); // API Call here
        item.close();
      } else {
        console.error('Save failed: ' + result.error.message);
      }
    });
  }

  mapUserToVisitorDto(user: User): Visitor {
    return {
      email: user.email,
      company: user.company,
      first_name: user.firstname,
      last_name: user.lastname,
      user: this.mailInfo.user,
      phone: user.phonenumber,
      reason: this.mailInfo.subject,
      start: this.mailInfo.start,
      end: this.mailInfo.end,
      location: this.mailInfo.location,
      id: user.visitorId
    } as Visitor;
  }

  ngOnDestroy(): void {
    if (this.polling) {
      clearInterval(this.polling);
    }
  }

  getVisitorQRCode(visitorId: string): string {
    const QRCode = require('qrcode');
    var opts = {
      errorCorrectionLevel: 'H',
      type: 'image/jpeg',
      quality: 0.9,
      margin: 1,
      color: {
        dark:"#55678f",
        light:"#f5f6f8"
      }
    }
    let dataUrl = '';
    QRCode.toDataURL(visitorId, opts, (err, url) => {
      if (err) {
        console.error(err);
      } else {
         dataUrl = url;
      }
    });
    return dataUrl;
  }

  generateAndAttachVistitorPass(vistitor: Visitor) {
    const doc = new jsPDF({
      orientation: 'landscape', // Landscape for the card format
      unit: 'px',
      format: [400, 250] // Adjust dimensions as per the design
    });
  
    // Drawing a rectangle for the page outline
    doc.setLineWidth(1);
    doc.rect(10, 10, 380, 230); // Adjust based on page size and padding
  
    // Load the visitor's photo (assuming you have a base64 string for the image)
    const visitorPhotoBase64 = 'data:image/png;base64,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';
  
    // Place the photo at the appropriate location (e.g., x: 40, y: 40)
    doc.addImage(visitorPhotoBase64, 'PNG', 30, 20, 100, 100); // Photo position and dimensions
  
    // Add text information
    doc.setFont('Helvetica', 'bold');
    doc.setTextColor('#55678f');
    doc.setFontSize(14);
    doc.text(vistitor.first_name + ' ' + vistitor.last_name, 40, 150); // Name Position
    doc.setFont('Helvetica', 'normal');
    doc.setFontSize(12);
    doc.text(vistitor.company, 40, 170); // Company
    doc.text(`Visiting ${vistitor.user}`, 40, 185); // Host
    doc.text('Date & Time: ' + this.datePipe.transform(vistitor.start, 'dd.MM.yyyy HH:mm'), 40, 200); // Date & Time
    doc.text(`Visitor ID: ${vistitor.visitor_id}`, 40, 215); // Visitor ID
  
    // Load and add the QR code
    const qrCodeBase64 = this.getVisitorQRCode(vistitor.visitor_id);
    doc.addImage(qrCodeBase64, 'JPEG', 230, 80, 140, 140); // QR code position and dimensions

    const logoBase64 = 'data:image/png;base64,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';
    doc.addImage(logoBase64, 'PNG', 250, 20, 100, 40); // Logo placement

    const blob = doc.output('blob')
    this.addPdfAttachment(blob, vistitor);
  }

  removeAttachments() {
    // Get all attachments
    const item = Office.context.mailbox.item;
    if (item.itemType === Office.MailboxEnums.ItemType.Appointment) {
      item.getAttachmentsAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          // Filter attachments that end with suffix Vistor_Pass.pdf
          const attachmentsToRemove = result.value.map(attachment => attachment).filter(f => f.name.endsWith('Vistor_Pass.pdf'));

          // Remove each filtered attachment
          attachmentsToRemove.forEach(attachment => {
            Office.context.mailbox.item.removeAttachmentAsync(attachment.id, (result) => {
              if (result.status === Office.AsyncResultStatus.Failed) {
                console.error(`Failed to remove attachment: ${attachment.name}`);
              } else {
                console.log(`Removed attachment: ${attachment.name}`);
              }
            });
          });
        }
      });
    }
  }

  addPdfAttachment(pdfBlob: Blob, visitor: Visitor) {
    const reader = new FileReader();
    reader.onload = (event) => {
      const fileReader = event.target as FileReader; 
      const base64String = (fileReader.result as string).split(',')[1];
      Office.context.mailbox.item.addFileAttachmentFromBase64Async(
        base64String,
        `${visitor.first_name}_${visitor.last_name}_Vistor_Pass.pdf`,
        { isInline: false },
        (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            console.log('Attachment added successfully');
          } else {
            console.error('Failed to add attachment:', result.error.message);
          }
        }
      );
    };
    reader.readAsDataURL(pdfBlob);
  }   
}
